import React from 'react';
import { i18n } from 'config/locales';

import { validDate } from 'components/Section/History/dateranges';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import DateRange from 'components/Form/DateRange';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { SubstanceAlcoholNegativeItemModel } from 'forms/sections/substance/alcohol/negative/types';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import { DateModel } from 'forms/shared/types';
import Comments from 'components/Form/Comments';

interface Props extends ValidationElementProps, Partial<SubstanceAlcoholNegativeItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  applicantBirthdate?: DateModel
}

export default class NegativeImpact extends ValidationElement<Props> {
  update = (updateValues: Partial<SubstanceAlcoholNegativeItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        Occurred: this.props.Occurred,
        Circumstances: this.props.Circumstances,
        NegativeImpact: this.props.NegativeImpact,
        Used: this.props.Used,
        Comments: this.props.Comments,
        ...updateValues,
      });
    }
  };

  updateOccurred = (values: SubstanceAlcoholNegativeItemModel['Occurred']) => {
    this.update({ Occurred: values });
  };

  updateCircumstances = (values: SubstanceAlcoholNegativeItemModel['Circumstances']) => {
    this.update({ Circumstances: values });
  };

  updateNegativeImpact = (values: SubstanceAlcoholNegativeItemModel['NegativeImpact']) => {
    this.update({ NegativeImpact: values });
  };

  updateUsed = (values: SubstanceAlcoholNegativeItemModel['Used']) => {
    this.update({ Used: values });
  };

  updateComments = (values: SubstanceAlcoholNegativeItemModel['Comments']) => {
    this.update({ Comments: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <div className="negative-impact">
        <Field
          attribute={`${attribute}.Occurred`}
          title={i18n.t('substance.alcohol.negativeImpact.heading.occurred')}
          help="substance.alcohol.negativeImpact.help.occurred"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            attribute={`${attribute}.Occurred`}
            name="Occurred"
            className="occurred"
            {...this.props.Occurred}
            hideDay
            prefix={
              this.props.Used && validDate(this.props.Used.from) && validDate(this.props.Used.to)
                ? 'occurred'
                : undefined
            }
            maxDate={
              this.props.Used && validDate(this.props.Used.to)
                ? this.props.Used.to
                : { year: new Date().getFullYear() }
            }
            minDate={
              this.props.Used && validDate(this.props.Used.from)
                ? this.props.Used.from
                : this.props.applicantBirthdate
            }
            minDateEqualTo
            onUpdate={this.updateOccurred}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.alcohol.negativeImpact.heading.circumstances')}
          className="no-margin-bottom"
        />

        <Field
          attribute={`${attribute}.Circumstances`}
          title={i18n.t('substance.alcohol.negativeImpact.label.circumstances')}
          titleSize="label"
          className="no-margin-bottom"
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={`${attribute}.Circumstances`}
            name="Circumstances"
            className="circumstances"
            {...this.props.Circumstances}
            onUpdate={this.updateCircumstances}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.NegativeImpact`}
          title={i18n.t('substance.alcohol.negativeImpact.label.negativeImpact')}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="textarea"
        >
          <Textarea
            attribute={`${attribute}.NegativeImpact`}
            name="NegativeImpact"
            className="negative-impact-explanation"
            {...this.props.NegativeImpact}
            onUpdate={this.updateNegativeImpact}
            required={this.props.required}
            onError={this.props.onError}
          />
        </Field>

        <Field
          attribute={`${attribute}.Used`}
          title={i18n.t('substance.alcohol.negativeImpact.heading.used')}
          help="substance.alcohol.negativeImpact.help.used"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.Used`}
            name="Used"
            className="used"
            {...this.props.Used}
            minDateEqualTo
            onUpdate={this.updateUsed}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        >
        </Comments>
      </div>
    );
  }
}

NegativeImpact.defaultProps = {
  Comments: {},
  onError: (value, arr) => arr,
};
